'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';

export default function CtaSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('hidden-before-reveal');
          }
        });
      },
      { threshold: 0.2 }
    );

    const els = sectionRef?.current?.querySelectorAll('.reveal, .reveal-scale');
    els?.forEach((el) => {
      el?.classList?.add('hidden-before-reveal');
      observer?.observe(el);
    });

    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-32 bg-campusbg overflow-hidden">
      <div className="max-w-[1280px] mx-auto px-6">
        <div className="reveal relative hero-gradient noise-overlay rounded-2xl p-12 md:p-20 text-center overflow-hidden">
          {/* Background blobs */}
          <div
            className="blob absolute top-[-20%] left-[-10%] w-72 h-72 opacity-20"
            style={{ background: 'radial-gradient(circle, #38BDF8 0%, transparent 70%)' }}
          />
          <div
            className="blob absolute bottom-[-20%] right-[-10%] w-80 h-80 opacity-15"
            style={{
              background: 'radial-gradient(circle, #1A56DB 0%, transparent 70%)',
              animationDelay: '-3s',
            }}
          />

          <div className="relative z-10 space-y-8">
            {/* Eyebrow */}
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5">
              <span className="w-2 h-2 rounded-full bg-success animate-pulse" />
              <span className="text-white/80 text-xs font-700 uppercase tracking-wider">Exclusively for our college students</span>
            </div>

            <h2 className="font-display font-800 text-[clamp(36px,6vw,72px)] tracking-tight text-white leading-tight">
              Your laundry,<br />
              <span className="gradient-text">handled with care.</span>
            </h2>

            <p className="text-white/60 text-lg font-400 max-w-xl mx-auto">
              CampusWash is the official laundry service of our college, managed by the administration to make student life easier.
            </p>

            {/* CTA group */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
              <Link href="/dashboard" className="bg-white text-primary px-10 py-5 rounded-full font-800 text-lg hover:scale-105 transition-transform shadow-hero cursor-pointer cta-pulse">
                Schedule Pickup →
              </Link>
              <Link href="/login" className="text-white/70 hover:text-white text-sm font-600 transition-colors">
                Sign in to your account
              </Link>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap items-center justify-center gap-6 pt-6 opacity-60">
              {[
                { icon: '🏫', label: 'College Managed' },
                { icon: '📦', label: 'Fully Insured' },
                { icon: '♻️', label: 'Eco-Friendly' },
                { icon: '⭐', label: '4.9/5 Rating' },
              ]?.map((badge) => (
                <div key={badge?.label} className="flex items-center gap-2">
                  <span className="text-base">{badge?.icon}</span>
                  <span className="text-white text-xs font-600">{badge?.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}